import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/content";

const TABS = ["All", "Students", "Mentors", "Schools", "Partners"];

const MOMENTS = [
  {
    src: "https://customer-assets-agu9un31.emergentagent.net/job_youth-futures-2/artifacts/b5izo5wu_6.jpg",
    alt: "IBY facilitator speaking to students at a school career workshop",
    caption: "Career day — speaking to students",
  },
  {
    src: "https://customer-assets-agu9un31.emergentagent.net/job_youth-futures-2/artifacts/8f92zu6t_Copy%20of%20343560360_784605526533118_6998788295990713653_n.webp",
    alt: "IBY mentors introducing themselves at a classroom workshop",
    caption: "IBY Inspire workshop in session",
  },
  {
    src: "https://customer-assets-agu9un31.emergentagent.net/job_youth-futures-2/artifacts/a6hholg1_Copy%20of%2020221012_140557.webp",
    alt: "A mentor in a one-on-one conversation with two students",
    caption: "1:1 mentoring — face to face",
  },
  {
    src: "https://customer-assets-agu9un31.emergentagent.net/job_youth-futures-2/artifacts/55nb52sn_IMG_3373.webp",
    alt: "IBY mentors and team together after a school session",
    caption: "Mentors after a school visit",
  },
];

const MomentImg = ({ m, className = "", testId }) => (
  <figure className={className} data-testid={testId}>
    <div className="overflow-hidden rounded-xl">
      <img src={m.src} alt={m.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
    </div>
    <figcaption className="mt-3 text-[11px] uppercase tracking-[0.2em] font-bold text-[#4A5568]">
      {m.caption}
    </figcaption>
  </figure>
);

const WorkshopMoments = () => (
  <section className="bg-white py-24 md:py-32 border-t border-black/5" data-testid="workshop-moments-section">
    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>Workshop Moments</Overline>
        <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-[#0B132B] max-w-2xl">
          The rooms where it happens.
        </h2>
        <p className="mt-5 text-[#4A5568] leading-relaxed max-w-xl">
          Snapshots from real IBY workshops, mentoring sessions and school visits — the everyday
          moments behind every testimonial on this page.
        </p>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-5 md:gap-8 mt-16 items-start">
        <Reveal className="hidden md:flex md:col-span-1 justify-center pt-4">
          <span className="font-display font-bold text-4xl tracking-[0.05em] text-[#0B132B] [writing-mode:vertical-rl] rotate-180 select-none">
            WORKSHOPS
          </span>
        </Reveal>

        <Reveal delay={0.05} className="col-span-1 md:col-span-4 md:mt-20">
          <MomentImg m={MOMENTS[0]} className="[&_img]:aspect-square" testId="moment-img-0" />
        </Reveal>

        <Reveal delay={0.1} className="col-span-1 md:col-span-7">
          <span className="hidden md:block font-display font-bold text-7xl lg:text-8xl leading-none tracking-tighter text-[#0B132B] select-none">
            REAL
          </span>
          <MomentImg m={MOMENTS[1]} className="md:mt-6 [&_img]:aspect-[4/3] md:[&_img]:aspect-[16/9]" testId="moment-img-1" />
        </Reveal>

        <Reveal delay={0.1} className="col-span-1 md:col-start-2 md:col-span-5 md:-mt-4">
          <MomentImg m={MOMENTS[2]} className="[&_img]:aspect-[4/3]" testId="moment-img-2" />
        </Reveal>

        <Reveal delay={0.15} className="col-span-1 md:col-span-6 md:mt-16">
          <MomentImg m={MOMENTS[3]} className="[&_img]:aspect-[4/3] md:[&_img]:aspect-[16/9]" testId="moment-img-3" />
          <span className="hidden md:block font-display font-bold text-7xl lg:text-8xl leading-none tracking-tighter text-[#0B132B] mt-6 select-none">
            MOMENTS<span className="text-[#FF5A36]">.</span>
          </span>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <p className="text-center text-sm text-[#4A5568] max-w-md mx-auto mt-16 leading-relaxed">
          Real access — not just inspiration. Every session, every conversation, every first step
          captured here started with someone showing up.
        </p>
      </Reveal>
    </div>
  </section>
);

export default function Testimonials() {
  const [tab, setTab] = useState("All");
  const items = tab === "All" ? TESTIMONIALS : TESTIMONIALS.filter((t) => t.type === tab);

  return (
    <main data-testid="testimonials-page">
      <section className="bg-[#0B132B] text-white pt-40 pb-24 grain relative overflow-hidden" data-testid="testimonials-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <Reveal>
            <Overline light>Testimonials</Overline>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-4xl">
              Don't take our word for it. Take theirs.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <Reveal>
          <div className="flex flex-wrap gap-3 mb-16" data-testid="testimonial-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`testimonial-tab-${t.toLowerCase()}`}
                className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${
                  tab === t
                    ? "bg-[#FF5A36] text-white"
                    : "bg-white border border-black/10 text-[#4A5568] hover:border-[#FF5A36]/50 hover:text-[#FF5A36]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {items.map((t, i) => (
              <motion.div
                layout
                key={t.name + t.role}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className="bg-white border border-black/5 rounded-[1.75rem] p-8 flex flex-col hover:-translate-y-1.5 transition-transform duration-500"
                data-testid={`testimonial-card-${i}`}
              >
                <Quote size={30} className="text-[#FF5A36] fill-[#FF5A36] mb-5" />
                <p className="text-[#374151] leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-4 mt-8 pt-6 border-t border-black/5">
                  <div>
                    <p className="font-bold text-[#0B132B] text-sm">{t.name}</p>
                    <p className="text-xs text-[#4A5568]">{t.role}</p>
                  </div>
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-[#FFB323]/15 text-[#B07500]">
                    {t.type}
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <WorkshopMoments />
    </main>
  );
}
